import { getHomepageSection, saveHomepageSection } from '../../../lib/cms'

const SECTIONS = [
  { id: 'hero',       label: 'Hero sekcija',       icon: 'ri-layout-top-2-line' },
  { id: 'about',      label: 'O nama',              icon: 'ri-team-line' },
  { id: 'solutions',  label: 'Rešenja (4 kartice)', icon: 'ri-lightbulb-line' },
  { id: 'categories', label: 'Kategorije',          icon: 'ri-grid-line' },
  { id: 'cta',        label: 'CTA sekcija',         icon: 'ri-megaphone-line' },
  { id: 'footer',     label: 'Footer',              icon: 'ri-layout-bottom-2-line' },
]

export default function AdminHomepage() {
  const [activeSection, setActiveSection] = useState('hero')
  const [content, setContent] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  const loadSection = async (id: string) => {
    setLoading(true)
    const data = await getHomepageSection(id)
    setContent(data)
    setLoading(false)
  }

  useEffect(() => { loadSection(activeSection) }, [activeSection])

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 3000) }

  const handleSave = async () => {
    setSaving(true)
    await saveHomepageSection(activeSection, content)
    flash('Sekcija sačuvana!')
    setSaving(false)
  }

  const setField = (path: string, value: any) => {
    setContent((prev: any) => {
      const parts = path.split('.')
      if (parts.length === 1) return { ...prev, [path]: value }
      if (parts.length === 2) return { ...prev, [parts[0]]: { ...prev[parts[0]], [parts[1]]: value } }
      return prev
    })
  }

  const updateArrayItem = (key: string, idx: number, field: string, value: any) => {
    setContent((prev: any) => {
      const arr = [...(prev[key] ?? [])]
      arr[idx] = { ...arr[idx], [field]: value }
      return { ...prev, [key]: arr }
    })
  }

  const addArrayItem = (key: string, template: any) => {
    setContent((prev: any) => ({
      ...prev,
      [key]: [...(prev[key] ?? []), template],
    }))
  }

  const removeArrayItem = (key: string, idx: number) => {
    setContent((prev: any) => ({
      ...prev,
      [key]: (prev[key] ?? []).filter((_: any, i: number) => i !== idx),
    }))
  }

  return (
    <div className="flex gap-6">
      {/* Section list */}
      <div className="w-48 flex-shrink-0">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm text-left border-b border-gray-100 last:border-0 transition ${
                activeSection === s.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className={`${s.icon} text-base`} />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 min-w-0">
        {msg && <div className="mb-4 bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">{msg}</div>}

        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-400">
            <i className="ri-loader-4-line animate-spin text-2xl" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">
                {SECTIONS.find(s => s.id === activeSection)?.label}
              </h2>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
              >
                {saving ? 'Čuvanje...' : 'Sačuvaj'}
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* HERO */}
              {activeSection === 'hero' && (
                <>
                  <Field label="Badge (oznaka)" value={content.badge} onChange={v => setField('badge', v)} />
                  <Field label="Naslov (koristite \n za novi red)" value={content.title} onChange={v => setField('title', v)} textarea />
                  <Field label="Podnaslov" value={content.subtitle} onChange={v => setField('subtitle', v)} textarea />
                  <Field label="Dugme 1 (primarno)" value={content.ctaPrimary} onChange={v => setField('ctaPrimary', v)} />
                  <Field label="Dugme 2 (sekundarno)" value={content.ctaSecondary} onChange={v => setField('ctaSecondary', v)} />
                  <Field label="Bottom slogan" value={content.bottomSlogan} onChange={v => setField('bottomSlogan', v)} />
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Statistike (4 stavke)</label>
                      <button type="button" onClick={() => addArrayItem('stats', { value: '', label: '' })} className="text-blue-600 text-xs hover:underline">+ Dodaj</button>
                    </div>
                    {(content.stats ?? []).map((s: any, i: number) => (
                      <div key={i} className="flex gap-2 mb-2">
                        <input className="input w-24" placeholder="Vrednost" value={s.value} onChange={e => updateArrayItem('stats', i, 'value', e.target.value)} />
                        <input className="input flex-1" placeholder="Oznaka" value={s.label} onChange={e => updateArrayItem('stats', i, 'label', e.target.value)} />
                        <button type="button" onClick={() => removeArrayItem('stats', i)} className="text-red-400 hover:text-red-600 px-2"><i className="ri-delete-bin-line" /></button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ABOUT */}
              {activeSection === 'about' && (
                <>
                  <Field label="Label (oznaka)" value={content.label} onChange={v => setField('label', v)} />
                  <Field label="Naslov" value={content.title} onChange={v => setField('title', v)} />
                  <Field label="Opis" value={content.description} onChange={v => setField('description', v)} textarea rows={4} />
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Statistike</label>
                      <button type="button" onClick={() => addArrayItem('stats', { value: '', label: '' })} className="text-blue-600 text-xs hover:underline">+ Dodaj</button>
                    </div>
                    {(content.stats ?? []).map((s: any, i: number) => (
                      <div key={i} className="flex gap-2 mb-2">
                        <input className="input w-24" placeholder="Vrednost" value={s.value} onChange={e => updateArrayItem('stats', i, 'value', e.target.value)} />
                        <input className="input flex-1" placeholder="Oznaka" value={s.label} onChange={e => updateArrayItem('stats', i, 'label', e.target.value)} />
                        <button type="button" onClick={() => removeArrayItem('stats', i)} className="text-red-400 hover:text-red-600 px-2"><i className="ri-delete-bin-line" /></button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* CTA */}
              {activeSection === 'cta' && (
                <>
                  <Field label="Naslov" value={content.title} onChange={v => setField('title', v)} />
                  <Field label="Opis" value={content.description} onChange={v => setField('description', v)} textarea />
                  <Field label="Tekst dugmeta" value={content.button} onChange={v => setField('button', v)} />
                </>
              )}

              {/* SOLUTIONS */}
              {activeSection === 'solutions' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">Uredi sekcije rešenja</p>
                    <button type="button" onClick={() => addArrayItem('', { title: '', subtitle: '', description: '', icon: '', image: '' })} className="text-blue-600 text-xs hover:underline hidden">+ Dodaj</button>
                  </div>
                  {(Array.isArray(content) ? content : []).map((s: any, i: number) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <p className="text-sm font-medium text-gray-700">Rešenje {i + 1}</p>
                      <Field label="Naslov" value={s.title} onChange={v => { const arr = [...(content as any[])]; arr[i] = { ...arr[i], title: v }; setContent(arr) }} />
                      <Field label="Podnaslov" value={s.subtitle} onChange={v => { const arr = [...(content as any[])]; arr[i] = { ...arr[i], subtitle: v }; setContent(arr) }} />
                      <Field label="Opis" value={s.description} onChange={v => { const arr = [...(content as any[])]; arr[i] = { ...arr[i], description: v }; setContent(arr) }} textarea />
                      <Field label="Ikona (remixicon klasa)" value={s.icon} onChange={v => { const arr = [...(content as any[])]; arr[i] = { ...arr[i], icon: v }; setContent(arr) }} />
                      <Field label="URL slike" value={s.image} onChange={v => { const arr = [...(content as any[])]; arr[i] = { ...arr[i], image: v }; setContent(arr) }} />
                    </div>
                  ))}
                  {!Array.isArray(content) && (
                    <p className="text-gray-400 text-sm">Sekcija Solutions sadrži niz — učitaj ponovo da bi se prikazala.</p>
                  )}
                </div>
              )}

              {/* CATEGORIES */}
              {activeSection === 'categories' && (
                <div className="space-y-6">
                  {(Array.isArray(content) ? content : []).map((c: any, i: number) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <p className="text-sm font-medium text-gray-700">Kategorija {i + 1}</p>
                      <Field label="Naslov" value={c.title} onChange={v => { const arr = [...(content as any[])]; arr[i] = { ...arr[i], title: v }; setContent(arr) }} />
                      <Field label="Podnaslov" value={c.subtitle} onChange={v => { const arr = [...(content as any[])]; arr[i] = { ...arr[i], subtitle: v }; setContent(arr) }} />
                      <Field label="Opis" value={c.description} onChange={v => { const arr = [...(content as any[])]; arr[i] = { ...arr[i], description: v }; setContent(arr) }} />
                    </div>
                  ))}
                </div>
              )}

              {/* FOOTER */}
              {activeSection === 'footer' && (
                <>
                  <Field label="Naziv kompanije" value={content.company} onChange={v => setField('company', v)} />
                  <Field label="Tagline" value={content.tagline} onChange={v => setField('tagline', v)} />
                  <Field label="Copyright tekst" value={content.copyright} onChange={v => setField('copyright', v)} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ label, value, onChange, textarea = false, rows = 3 }: {
  label: string
  value: any
  onChange: (v: string) => void
  textarea?: boolean
  rows?: number
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {textarea
        ? <textarea className="input" rows={rows} value={value ?? ''} onChange={e => onChange(e.target.value)} />
        : <input className="input" value={value ?? ''} onChange={e => onChange(e.target.value)} />
      }
    </div>
  )
}
